import React from 'react';
import { Box, Paper, Typography, Chip, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import MissingLabourItemsCreator from './MissingLabourItemsCreator';
import PlotPreviewAccordion from './PlotPreviewAccordion';
import { formatCurrency } from '../../../../../../shared/utils/currency';
import { SiteImportPreviewResponse } from '../../../../api/sitesApi';
import { SiteImportFormData } from '../../../../schemas/siteImportSchema';

interface ReviewConfirmStepProps {
  previewData: SiteImportPreviewResponse | null;
  importPath: 'template' | 'csv' | null;
  onAllResolved: () => void;
}

const ReviewConfirmStep: React.FC<ReviewConfirmStepProps> = ({
  previewData,
  importPath,
  onAllResolved,
}) => {
  const { getValues } = useFormContext<SiteImportFormData>();
  const { name, slug, what3words } = getValues();

  if (!previewData) {
    return (
      <Paper sx={{ p: 3 }} data-testid="confirm-import-step">
        <Typography variant="body1" color="error">
          No preview data available. Please complete the previous steps first.
        </Typography>
      </Paper>
    );
  }

  const totalItems = previewData.plots.reduce(
    (sum, plot) => sum + plot.labourItems.length,
    0
  );
  const totalValue = previewData.plots.reduce(
    (sum, plot) =>
      sum + plot.labourItems.reduce((itemSum, item) => itemSum + item.value, 0),
    0
  );

  return (
    <Stack spacing={2} data-testid="confirm-import-step">
      {/* Summary card */}
      <Paper
        sx={{
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
          p: 2.5,
          borderRadius: 2,
        }}
        data-testid="import-summary"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Site name
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {name}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Site code
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ fontFamily: 'monospace' }}
            >
              {slug}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Location
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {what3words || 'Not provided'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Source
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {importPath === 'template' ? 'Trade template' : 'CSV upload'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Missing labour items */}
      {previewData.missingLabourDefinitions &&
        previewData.missingLabourDefinitions.length > 0 && (
          <MissingLabourItemsCreator
            missingItems={previewData.missingLabourDefinitions}
            onAllResolved={onAllResolved}
          />
        )}

      {/* Stats row */}
      <Stack direction="row" spacing={1}>
        <Chip
          label={`${previewData.plots.length} plots`}
          size="small"
          color="info"
          variant="outlined"
        />
        <Chip
          label={`${totalItems} labour items`}
          size="small"
          color="info"
          variant="outlined"
        />
        <Chip
          label={`${formatCurrency(totalValue)} total`}
          size="small"
          color="success"
          variant="outlined"
        />
      </Stack>

      {/* Preview table */}
      <PlotPreviewAccordion plots={previewData.plots} />
    </Stack>
  );
};

export default ReviewConfirmStep;
